import React, { useState, useEffect } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, ReferenceLine } from 'recharts';
import Task from '../assets/icons/Task.png';
import Sales from '../assets/icons/TotalSales.png';
import People from '../assets/icons/People.png';
import Box from '../assets/icons/Boxes.png';
import ProductPlaceholder from '../assets/images/product.jpg';
import { StatRowSkeleton } from '../components/skeleton/PageSkeletons';

function Dashboard() {
  const [statisticsData, setStatisticsData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [salesSummary, setSalesSummary] = useState({ totalSales: 0, totalOrders: 0 });
  const [categoryData, setCategoryData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrdersByPeriod, setRecentOrdersByPeriod] = useState({ today: [], yesterday: [], week: [] });
  const [recentOrdersPeriod, setRecentOrdersPeriod] = useState('today');
  const [recentOrdersLoading, setRecentOrdersLoading] = useState(true);
  const [errorStatistics, setErrorStatistics] = useState(null);
  const [errorSales, setErrorSales] = useState(null);
  const [errorCategories, setErrorCategories] = useState(null);
  const [errorTopProducts, setErrorTopProducts] = useState(null);
  const [errorRecentOrders, setErrorRecentOrders] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [topProductsLoading, setTopProductsLoading] = useState(true);
  const months = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];

  const getDefaultMonth = () => months[new Date().getMonth()];
  const getDefaultYear = () => String(new Date().getFullYear());
  const getYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 2019 }, (_, i) => String(2020 + i));
  };

  const [selectedMonth, setSelectedMonth] = useState(() => getDefaultMonth());
  const [selectedYear, setSelectedYear] = useState(() => getDefaultYear());
  const years = getYears();

  const monthToNumber = (month) => {
    const monthMap = {
      january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
      july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
    };
    return monthMap[month.toLowerCase()];
  };

  const getCurrentWeekRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return { startOfWeek, endOfWeek };
  };

  const getTodayRange = () => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return { startOfDay, endOfDay };
  };

  const getYesterdayRange = () => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const startOfDay = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
    return { startOfDay, endOfDay };
  };

  useEffect(() => {
    const fetchStatistics = async () => {
      setErrorStatistics(null);
      try {
        const response = await fetch("https://api.alburaqscents.com/v1/order/analytic");
        const result = await response.json();
        if (result.success) {
          setStatisticsData(result.data);
        } else {
          setErrorStatistics('Failed to fetch statistics');
        }
      } catch (err) {
        setErrorStatistics('Error fetching statistics: ' + err.message);
      }
    };
    fetchStatistics();
  }, []);

  useEffect(() => {
    const fetchSalesData = async () => {
      setErrorSales(null);
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const requestOptions = {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
          redirect: "follow"
        };

        const response = await fetch("https://api.alburaqscents.com/v1/order/sales", requestOptions);
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error('Sales Non-JSON response:', text.slice(0, 200));
          throw new Error(`Sales response is not JSON (Status: ${response.status})`);
        }

        const result = await response.json();
        if (result.success) {
          const data = result.data || [];
          setSalesData(data.map(item => ({
            name: item.day,
            sales: (item.sales || 0) / 1000,
            totalOrder: item.totalOrder ?? 0
          })));
          const totalSales = data.reduce((sum, item) => sum + (Number(item.sales) || 0), 0);
          const totalOrders = data.reduce((sum, item) => sum + (Number(item.totalOrder) || 0), 0);
          setSalesSummary({ totalSales, totalOrders });
        } else {
          setErrorSales('Failed to fetch sales data: ' + (result.message || 'Unknown error'));
        }
      } catch (err) {
        setErrorSales('Error fetching sales data: ' + err.message);
        setSalesSummary({ totalSales: 0, totalOrders: 0 });
      }
    };
    fetchSalesData();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchCategoryData = async () => {
      setCategoryLoading(true);
      setErrorCategories(null);
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const requestOptions = {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
          redirect: "follow"
        };

        const response = await fetch("https://api.alburaqscents.com/v1/order/category-statics", requestOptions);
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error('Category Non-JSON response:', text.slice(0, 200));
          throw new Error(`Category response is not JSON (Status: ${response.status})`);
        }

        const result = await response.json();
        const rawData = result.success && Array.isArray(result.data) ? result.data : (Array.isArray(result) ? result : []);
        const mapped = rawData
          .map(item => ({
            name: item.category ?? item.name ?? item.categoryName ?? 'Other',
            value: Number(item.soldCount ?? item.value ?? item.count ?? item.quantity ?? item.total ?? item.sales ?? 0) || 0
          }))
          .filter(item => item.value > 0)
          .sort((a, b) => b.value - a.value);
        setCategoryData(mapped);
      } catch (err) {
        setErrorCategories('Error fetching category data: ' + err.message);
        setCategoryData([]);
      } finally {
        setCategoryLoading(false);
      }
    };
    fetchCategoryData();
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchTopProducts = async () => {
      setTopProductsLoading(true);
      setErrorTopProducts(null);
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const requestOptions = {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            month: monthToNumber(selectedMonth),
            year: parseInt(selectedYear)
          }),
          redirect: "follow"
        };

        const response = await fetch("https://api.alburaqscents.com/v1/order/top-products", requestOptions);
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error('Top Products Non-JSON response:', text.slice(0, 200));
          throw new Error(`Top Products response is not JSON (Status: ${response.status})`);
        }

        const result = await response.json();
        if (result.success) {
          const data = result.data || [];
          setTopProducts(data.map(item => ({
            name: item.name,
            price: (item.totalRevenue ?? 0).toString(),
            image: item.image || ProductPlaceholder
          })));
        } else {
          setErrorTopProducts('Failed to fetch top products: ' + (result.message || 'Unknown error'));
          setTopProducts([]);
        }
      } catch (err) {
        setErrorTopProducts('Error fetching top products: ' + err.message);
        setTopProducts([]);
      } finally {
        setTopProductsLoading(false);
      }
    };
    fetchTopProducts();
  }, [selectedMonth, selectedYear]);

  const mapOrderToDisplay = (order) => ({
    invoiceNo: order.orderNumber,
    orderTime: new Date(order.createdAt).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }),
    customerName: order.customerId ? order.customerId.name || 'Unknown' : 'Guest',
    price: (order.totalAmount ?? 0).toString(),
    paymentMethod: order.paymentMethod ?? '—',
    number: order.shippingAddress?.phoneNumber ?? '—',
    city: order.shippingAddress?.city ?? '—'
  });

  const isActiveOrder = (order) =>
    order.orderStatus !== 'delivered' && order.orderStatus !== 'cancelled' && order.orderStatus !== 'canceled';

  useEffect(() => {
    const fetchRecentOrders = async () => {
      setRecentOrdersLoading(true);
      setErrorRecentOrders(null);
      try {
        const requestOptions = { method: "GET", redirect: "follow" };
        const response = await fetch("https://api.alburaqscents.com/v1/order", requestOptions);
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error('Recent Orders Non-JSON response:', text.slice(0, 200));
          throw new Error(`Recent Orders response is not JSON (Status: ${response.status})`);
        }

        const result = await response.json();
        if (result.success) {
          const orders = result.data || [];
          const { startOfWeek, endOfWeek } = getCurrentWeekRange();
          const { startOfDay: todayStart, endOfDay: todayEnd } = getTodayRange();
          const { startOfDay: yesterdayStart, endOfDay: yesterdayEnd } = getYesterdayRange();

          const inRange = (createdAt, start, end) => {
            const d = new Date(createdAt);
            return d >= start && d <= end;
          };

          const todayOrders = orders
            .filter(order => isActiveOrder(order) && inRange(order.createdAt, todayStart, todayEnd))
            .map(mapOrderToDisplay);

          const yesterdayOrders = orders
            .filter(order => isActiveOrder(order) && inRange(order.createdAt, yesterdayStart, yesterdayEnd))
            .map(mapOrderToDisplay);

          const weekOrders = orders
            .filter(order => isActiveOrder(order) && inRange(order.createdAt, startOfWeek, endOfWeek))
            .map(mapOrderToDisplay);

          setRecentOrdersByPeriod({ today: todayOrders, yesterday: yesterdayOrders, week: weekOrders });
        } else {
          setErrorRecentOrders('Failed to fetch recent orders: ' + (result.message || 'Unknown error'));
        }
      } catch (err) {
        setErrorRecentOrders('Error fetching recent orders: ' + err.message);
      } finally {
        setRecentOrdersLoading(false);
      }
    };
    fetchRecentOrders();
  }, []);

  const statistics = statisticsData ? [
    { title: 'Today Orders', value: statisticsData.orders.today.toString(), icon: Task },
    { title: 'Yesterday Orders', value: statisticsData.orders.yesterday.toString(), icon: Sales },
    { title: 'Monthly Orders', value: statisticsData.orders.thisMonth.toString(), icon: People },
    { title: 'Total Orders', value: statisticsData.orders.total.toString(), icon: Box },
  ] : [];

  const PIE_COLORS = ['#3FD5D1', '#F27069', '#F9D978', '#A78BFA', '#34D399', '#F472B6', '#FBBF24', '#60A5FA'];

  return (
    <div style={styles.dashboardContainer}>
      <div style={styles.headerContainer}>
        <h1 style={styles.headerTitle}>Dashboard</h1>
        <p style={styles.headerSubtitle}>Welcome to your dashboard</p>
      </div>

      {statisticsData === null && !errorStatistics ? (
        <StatRowSkeleton count={4} variant="icon" className="skeleton-stat-row--dashboard" />
      ) : (
      <div style={styles.statisticsRowContainer}>
        {errorStatistics ? (
          <p style={styles.noDataText}>{errorStatistics}</p>
        ) : statistics.length > 0 ? (
          statistics.map((stat, index) => (
            <div key={index} style={styles.statisticBox}>
              <div>
                <p style={styles.statisticValue}>{stat.value}</p>
                <p style={styles.statisticTitle}>{stat.title}</p>
              </div>
              <div style={styles.statisticIcon}>
                <img src={stat.icon} alt={stat.title} style={{ width: '60%', height: '60%', objectFit: 'contain' }} />
              </div>
            </div>
          ))
        ) : (
          <p style={styles.noDataText}>No statistics available</p>
        )}
      </div>
      )}

      <div style={{ ...styles.commonSectionStyle, ...styles.salesChartSectionOverrides }}>
        <div style={styles.salesChartHeader}>
          <h2 style={styles.salesChartTitle}>Sales Statistics</h2>
          <div style={styles.salesChartControls}>
            <select 
              style={styles.dropdown}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {months.map((month) => (
                <option key={month} value={month}>{month.charAt(0).toUpperCase() + month.slice(1)}</option>
              ))}
            </select>
            <select 
              style={styles.dropdown}
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {years.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        {salesData.length > 0 && !errorSales && (
          <div style={styles.salesSummaryRow}>
            <div style={styles.salesSummaryItem}>
              <span style={styles.salesSummaryLabel}>Total Sales</span>
              <span style={styles.salesSummaryValue}>PKR {salesSummary.totalSales.toLocaleString()}</span>
            </div>
            <div style={styles.salesSummaryItem}>
              <span style={styles.salesSummaryLabel}>Total Orders</span>
              <span style={styles.salesSummaryValue}>{salesSummary.totalOrders.toLocaleString()}</span>
            </div>
          </div>
        )}
        <div style={styles.chartArea}>
          {salesData.length === 0 && !errorSales ? (
            <div style={styles.skeletonChart}></div>
          ) : errorSales ? (
            <p style={styles.noDataText}>{errorSales}</p>
          ) : salesData.length > 0 ? (
            <>
              <svg width="60" height="20" style={{ position: 'absolute', top: '20px', left: '100px', zIndex: 1 }}>
                <rect x="0" y="5" width="20" height="10" fill="#00d418" rx="2" ry="2"/>
                <text x="25" y="15" fill="#E0E0E0" fontSize="12">Sales (k)</text>
              </svg>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={salesData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00d418" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#00d418" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={true} horizontal={true} />
                  <XAxis dataKey="name" stroke="#E0E0E0" tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#E0E0E0"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => value.toFixed(1)}
                    domain={[0, 'auto']}
                    tickCount={8}
                  />
                  <Tooltip
                    cursor={{ stroke: '#666', strokeWidth: 1 }}
                    contentStyle={{ backgroundColor: '#444', border: 'none', color: '#eee', borderRadius: '4px' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0]?.payload;
                      return (
                        <div style={{ padding: '4px 0' }}>
                          <div style={{ color: '#F9D978', marginBottom: '6px' }}>Day {label}</div>
                          <div style={{ color: '#00d418' }}>Sales: PKR {(p?.sales * 1000 || 0).toLocaleString()}</div>
                          <div style={{ color: '#E0E0E0' }}>Orders: {p?.totalOrder ?? 0}</div>
                        </div>
                      );
                    }}
                  />
                  <Area type="monotone" dataKey="sales" stroke="#00d418" fill="url(#colorSales)" strokeWidth={2} />
                  <Line
                    type="monotone"
                    dataKey="sales"
                    stroke="#00d418"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, fill: '#00d418', stroke: '#fff', strokeWidth: 2 }}
                  />
                  <ReferenceLine x={new Date().getDate().toString().padStart(2, '0')} stroke="#8884d8" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </>
          ) : (
            <p style={styles.noDataText}>No sales data available</p>
          )}
        </div>
      </div>

      <div style={styles.bottomSectionContainer}>
        <div style={styles.bottomSectionBox}>
          <div style={styles.bottomSectionHeader}>
            <h2 style={styles.bottomSectionTitle}>Most Selling Category</h2>
          </div>
          {categoryLoading ? (
            <div style={styles.skeletonPieChart}>
              <div style={styles.skeletonLegend}>
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} style={styles.skeletonLegendItem}>
                    <div style={styles.skeletonColorBox}></div>
                    <div style={styles.skeletonText}></div>
                  </div>
                ))}
              </div>
              <div style={styles.skeletonPieChartArea}></div>
            </div>
          ) : errorCategories ? (
            <p style={styles.noDataText}>{errorCategories}</p>
          ) : categoryData.length > 0 ? (
            (() => {
              const total = categoryData.reduce((sum, d) => sum + d.value, 0);
              return (
                <div style={styles.pieChartWrapper}>
                  <div style={styles.pieChartLegend}>
                    {categoryData.map((entry, index) => {
                      const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                      return (
                        <div key={`legend-${index}`} style={styles.legendItem}>
                          <span style={{ ...styles.legendColorBox, backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}></span>
                          <p style={styles.legendText}>{entry.name} <span style={{ color: '#888', fontSize: '0.9em' }}>({pct}%)</span></p>
                        </div>
                      );
                    })}
                  </div>
                  <div style={styles.pieChartArea}>
                    <ResponsiveContainer width={220} height={220}>
                      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={100}
                          paddingAngle={1}
                          dataKey="value"
                          cornerRadius={4}
                          stroke="#1a1a1a"
                          strokeWidth={2}
                          nameKey="name"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: '#444', border: 'none', color: '#eee', borderRadius: '4px' }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const entry = payload[0];
                            const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                            return (
                              <div style={{ padding: '8px 12px' }}>
                                <div style={{ color: '#F9D978', marginBottom: '4px' }}>{entry.name}</div>
                                <div style={{ color: '#00d418' }}>Sold: {entry.value}</div>
                                <div style={{ color: '#aaa', fontSize: '0.9em' }}>{pct}% of total</div>
                              </div>
                            );
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📊</div>
              <p style={styles.emptyStateTitle}>Not enough data</p>
              <p style={styles.emptyStateText}>Sales data for this period is insufficient to show category breakdown. Try another month or check back when you have more orders.</p>
            </div>
          )}
        </div>

        <div style={styles.bottomSectionBox}>
          <div style={styles.bottomSectionHeader}>
            <h2 style={styles.bottomSectionTitle}>Top Products</h2>
          </div>
          {topProductsLoading ? (
            <div style={styles.skeletonTable}>
              <div style={styles.skeletonTableHeader}>
                <div style={styles.skeletonText}></div>
                <div style={styles.skeletonText}></div>
              </div>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} style={styles.skeletonTableRow}>
                  <div style={styles.skeletonProductCell}>
                    <div style={styles.skeletonImage}></div>
                    <div style={styles.skeletonText}></div>
                  </div>
                  <div style={styles.skeletonText}></div>
                </div>
              ))}
            </div>
          ) : errorTopProducts ? (
            <p style={styles.noDataText}>{errorTopProducts}</p>
          ) : topProducts.length > 0 ? (
            <div style={styles.table}>
              <div style={styles.productTableHeader}>
                <span style={styles.productTableHeaderItem}>Product Name</span>
                <span style={styles.productTableHeaderItem}>Revenue</span>
              </div>
              {topProducts.map((product, index) => (
                <div key={index} style={styles.productTableRow}>
                  <div style={styles.productNameCell}>
                    <img src={product.image} alt={product.name} style={styles.productImage} />
                    <span>{product.name}</span>
                  </div>
                  <span style={styles.productPriceCell}>{product.price}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📦</div>
              <p style={styles.emptyStateTitle}>Not enough data</p>
              <p style={styles.emptyStateText}>No product sales in this period. Try selecting another month or year when you have more orders.</p>
            </div>
          )}
        </div>
      </div>

      <div style={styles.commonSectionStyle}>
        <div style={styles.recentOrdersHeader}>
          <h2 style={styles.sectionTitle}>Recent Orders</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {['today', 'yesterday', 'week'].map((period) => (
              <button
                key={period}
                onClick={() => setRecentOrdersPeriod(period)}
                style={{
                  ...styles.recentOrderTab,
                  ...(recentOrdersPeriod === period ? styles.recentOrderTabActive : {}),
                }}
              >
                {period === 'today' ? 'Today' : period === 'yesterday' ? 'Yesterday' : 'This Week'}
              </button>
            ))}
            <button style={styles.viewAllButton}>View All</button>
          </div>
        </div>
        {recentOrdersLoading ? (
          <div style={styles.skeletonTable}>
            <div style={styles.skeletonTableHeader}>
              {Array.from({ length: 7 }).map((_, index) => (
                <div key={index} style={styles.skeletonText}></div>
              ))}
            </div>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} style={styles.skeletonTableRow}>
                {Array.from({ length: 7 }).map((_, cellIndex) => (
                  <div key={cellIndex} style={styles.skeletonText}></div>
                ))}
              </div>
            ))}
          </div>
        ) : errorRecentOrders ? (
          <p style={styles.noDataText}>{errorRecentOrders}</p>
        ) : (() => {
          const list = recentOrdersByPeriod[recentOrdersPeriod] || [];
          return list.length > 0 ? (
            <div style={styles.tableWrapper}>
              <table style={styles.ordersTable}>
                <thead>
                  <tr style={styles.ordersTableHeaderRow}>
                    <th style={styles.th}>Invoice No</th>
                    <th style={styles.th}>Order Time</th>
                    <th style={styles.th}>Customer Name</th>
                    <th style={styles.th}>Price</th>
                    <th style={styles.th}>Payment Method</th>
                    <th style={styles.th}>Number</th>
                    <th style={styles.th}>City</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((order, index) => (
                    <tr key={index} style={styles.ordersTableRow}>
                      <td style={styles.td}><span style={styles.invoiceNumber}>{order.invoiceNo}</span></td>
                      <td style={styles.td}>{order.orderTime}</td>
                      <td style={styles.td}>{order.customerName}</td>
                      <td style={styles.td}>{order.price}</td>
                      <td style={styles.td}>{order.paymentMethod}</td>
                      <td style={styles.td}>{order.number}</td>
                      <td style={styles.td}>{order.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={styles.noDataText}>
              No orders {recentOrdersPeriod === 'today' ? 'today' : recentOrdersPeriod === 'yesterday' ? 'yesterday' : 'this week'} (excluding delivered/cancelled)
            </p>
          );
        })()}
      </div>
    </div>
  );
}

const styles = {
  dashboardContainer: {
    fontFamily: 'Inter, sans-serif',
    backgroundColor: '#E5D0A5',
    color: '#000',
    padding: '20px',
    minHeight: '100vh',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  commonSectionStyle: {
    backgroundColor: '#000',
    padding: '20px',
    borderRadius: '20px',
  },
  salesChartSectionOverrides: {
    marginBottom: '30px',
  },
  headerContainer: {
    marginBottom: '30px',
  },
  headerTitle: {
    fontSize: '1.5em',
    margin: '0',
    fontWeight: 'bold',
    color: '#000',
  },
  headerSubtitle: {
    fontSize: '0.875em',
    color: '#000',
  },
  statisticsRowContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: '20px',
    flexWrap: 'wrap',
    marginBottom: '30px',
    width: '100%',
  },
  statisticBox: {
    backgroundColor: '#000',
    padding: '25px 15px',
    borderRadius: '20px',
    textAlign: 'center',
    flex: '1 1 200px',
    minWidth: '200px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statisticValue: {
    fontSize: '1.125em',
    fontWeight: '300',
    textAlign: 'left',
    margin: '0 0 10px 0',
    color: '#F9D978',
  },
  statisticTitle: {
    fontSize: '0.8125em',
    color: '#F9D978',
    margin: '0',
  },
  statisticIcon: {
    height: '70px',
    width: '70px',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: "100px",
    backgroundColor: '#F9D978',
    display: 'flex',
    overflow: 'hidden',
  },
  salesChartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  salesChartTitle: {
    fontSize: '1.125em',
    margin: '0',
    color: '#F9D978',
  },
  salesChartControls: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  salesSummaryRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  salesSummaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 20px',
    backgroundColor: 'rgba(0, 212, 24, 0.15)',
    borderRadius: '12px',
    border: '1px solid rgba(0, 212, 24, 0.4)',
  },
  salesSummaryLabel: {
    fontSize: '0.85em',
    color: '#B0B0B0',
  },
  salesSummaryValue: {
    fontSize: '1.0625em',
    fontWeight: '600',
    color: '#00d418',
  },
  dropdown: {
    backgroundColor: '#000',
    color: '#F9D978',
    border: '1px solid #F9D978',
    borderRadius: '20px',
    padding: '10px 20px',
    cursor: 'pointer',
    outline: 'none',
    width: '170px',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23F9D978\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\' class=\'feather feather-chevron-down\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    backgroundSize: '12px',
    paddingRight: '30px',
  },
  chartArea: {
    height: '450px',
    backgroundColor: '#000',
    borderRadius: '20px',
    padding: '20px',
    position: 'relative',
  },
  bottomSectionContainer: {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    flexWrap: 'wrap',
  },
  bottomSectionBox: {
    backgroundColor: '#000',
    padding: '20px',
    borderRadius: '20px',
    flex: '1',
    minWidth: '300px',
  },
  bottomSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  bottomSectionTitle: {
    fontSize: '1.0625em',
    margin: '0',
    color: '#F9D978',
  },
  bottomSectionDropdown: {
    backgroundColor: '#000',
    color: '#F9D978',
    border: '1px solid #F9D978',
    borderRadius: '20px',
    padding: '5px 15px',
    cursor: 'pointer',
    outline: 'none',
    width: '120px',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23F9D978\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\' class=\'feather feather-chevron-down\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '10px',
    paddingRight: '25px',
    fontSize: '0.9em',
  },
  pieChartWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'center',
    minHeight: '220px',
  },
  pieChartLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#E0E0E0',
    fontSize: '0.85em',
  },
  legendColorBox: {
    width: '12px',
    height: '12px',
    borderRadius: '3px',
  },
  legendText: {
    margin: '0',
  },
  pieChartArea: {
    width: 220,
    height: 220,
    minWidth: 220,
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  table: {
    width: '100%',
  },
  productTableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #444',
    marginBottom: '10px',
  },
  productTableHeaderItem: {
    fontWeight: 'normal',
    color: '#B0B0B0',
    fontSize: '0.9em',
  },
  productTableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #333',
  },
  productNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: '#F9D978',
    fontSize: '0.95em',
  },
  productImage: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    objectFit: 'cover',
    marginRight: '5px',
  },
  productPriceCell: {
    fontWeight: 'normal',
    color: '#FFFFFF',
    fontSize: '0.95em',
  },
  recentOrdersHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  recentOrderTab: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid #F9D978',
    backgroundColor: 'transparent',
    color: '#F9D978',
    cursor: 'pointer',
    fontSize: '0.9em',
    transition: 'background-color 0.2s, color 0.2s',
    outline: 'none',
  },
  recentOrderTabActive: {
    backgroundColor: '#F9D978',
    color: '#000',
  },
  sectionTitle: {
    fontSize: '1.125em',
    margin: '0',
    color: '#F9D978',
  },
  viewAllButton: {
    backgroundColor: 'black',
    border: 'none',
    color: '#F9D978',
    padding: '10px 20px',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
    outline: 'none',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  ordersTable: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px',
  },
  th: {
    padding: '15px 20px',
    textAlign: 'left',
    color: '#E5D0A5',
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    fontSize: '0.9em',
  },
  td: {
    padding: '25px 20px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    color: '#F9D978',
    fontSize: '0.85em',
  },
  ordersTableHeaderRow: {
    backgroundColor: '#000',
  },
  ordersTableRow: {
    backgroundColor: 'transparent',
    transition: 'background-color 0.3s ease',
  },
  invoiceNumber: {
    color: '#F9D978',
    fontWeight: 'bold',
  },
  noDataText: {
    color: '#F9D978',
    textAlign: 'center',
    fontSize: '1em',
    padding: '20px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '220px',
    padding: '32px 24px',
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: '32px',
    marginBottom: '16px',
    opacity: 0.7,
  },
  emptyStateTitle: {
    color: '#F9D978',
    fontSize: '0.95em',
    fontWeight: '600',
    margin: '0 0 8px 0',
  },
  emptyStateText: {
    color: '#888',
    fontSize: '0.9em',
    margin: 0,
    maxWidth: '280px',
    lineHeight: 1.5,
  },
  skeletonStatisticBox: {
    backgroundColor: '#000',
    padding: '25px 15px',
    borderRadius: '20px',
    flex: '1 1 200px',
    minWidth: '200px',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    animation: 'dashboardSkeletonPulse 1.5s ease-in-out infinite',
  },
  skeletonTextGroup: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  skeletonTextValue: {
    backgroundColor: '#2a2a2a',
    height: '18px',
    width: '52px',
    borderRadius: '4px',
    marginBottom: '10px',
    animation: 'dashboardSkeletonPulse 1.5s ease-in-out infinite',
  },
  skeletonTextTitle: {
    backgroundColor: '#2a2a2a',
    height: '13px',
    width: '96px',
    borderRadius: '4px',
    animation: 'dashboardSkeletonPulse 1.5s ease-in-out infinite',
  },
  skeletonIcon: {
    backgroundColor: '#2a2a2a',
    height: '70px',
    width: '70px',
    borderRadius: '100px',
    flexShrink: 0,
    marginLeft: '12px',
    animation: 'dashboardSkeletonPulse 1.5s ease-in-out infinite',
  },
  skeletonChart: {
    height: '450px',
    backgroundColor: '#222',
    borderRadius: '20px',
    animation: 'pulse 1.5s infinite',
  },
  skeletonPieChart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'center',
    minHeight: '220px',
  },
  skeletonLegend: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: '20px',
  },
  skeletonLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  skeletonColorBox: {
    backgroundColor: '#333',
    width: '12px',
    height: '12px',
    borderRadius: '3px',
    animation: 'pulse 1.5s infinite',
  },
  skeletonPieChartArea: {
    backgroundColor: '#333',
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    animation: 'pulse 1.5s infinite',
  },
  skeletonTable: {
    width: '100%',
  },
  skeletonTableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #444',
    marginBottom: '10px',
  },
  skeletonTableRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #333',
  },
  skeletonProductCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  skeletonImage: {
    backgroundColor: '#333',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    animation: 'pulse 1.5s infinite',
  },
};

export default Dashboard;